/* eslint-disable import/no-named-as-default-member */
import React from 'react'
import { connect } from 'react-redux'
import { Button, Table, Icon, Header, Divider } from 'semantic-ui-react'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import _ from 'lodash'
import firebase from '../../../config/firebase'

import ItemActions from '../../../reducers/itemReducer'
import ItemSyncActions from '../../../reducers/itemSyncReducer'

import Placeholder from './storePlaceholder'
import DraggableItem from './draggableItem'
import ItemForm from '../forms/itemForm'
import ItemImageCropUploader from '../../../lib/ItemImageCropUploader'

const initItemData = {
  name: '',
  current_price: 0,
  description: '',
  tag: '',
  code: '',
  status: 'publish',
}

class RestaurantItems extends React.PureComponent {
  state = {
    arrangeItems: [],
    arrangeOptions: [],
    hasArranged: false,
    itemData: null,
    modalVisible: false,
    action: null,
    uploadData: null,
    uploadVisible: false,
    showImage: localStorage.getItem('show-items-image') || 'show',
  }

  componentDidMount() {
    const { items, getItemSync, storeId } = this.props
    getItemSync(storeId)
    this.initRearrangeItems(items)
  }

  componentWillReceiveProps(nextProps) {
    const { items } = this.props

    if (nextProps.items !== items && nextProps.items) {
      this.initRearrangeItems(nextProps.items)
    }
  }

  initRearrangeItems = items => {
    const newItemsList = _.map(items, (item, itemKey) => ({
      ...item,
      itemKey,
    }))

    const orderByListing = _.orderBy(newItemsList, item => item.order_number, ['asc'])

    const arrangeItems = _.map(orderByListing, item => ({
      id: item.itemKey,
      content: item.name,
      order: item.order_number || '',
      itemData: item,
    }))

    this.setState({ arrangeItems, hasArranged: false })
  }

  getItemDraggableStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    padding: 10,
    margin: `4px 0 4px 0`,
    background: isDragging ? 'black' : '#e0e1e2',
    width: '100%',
    borderRadius: `5px`,
    color: isDragging ? 'white' : 'rgba(0,0,0,.8)',
    ...draggableStyle,
  })

  getItemTag = item => {
    const { tags } = this.props

    if (item && item.itemData.tag) {
      if (tags[item.itemData.tag]) {
        return tags[item.itemData.tag]
      }
    }

    return '...'
  }

  onToggleShowImage = () => {
    let { showImage } = this.state

    showImage = showImage === 'hide' ? 'show' : 'hide'

    localStorage.setItem('show-items-image', showImage)
    this.setState({ showImage })
  }

  onSaveOrder = () => {
    const { arrangeItems } = this.state
    const { storeId, saveOrder, itemSyncSetLastUpdate } = this.props

    const conf = window.confirm('Are you sure to update item list order')

    if (conf) {
      saveOrder(storeId, arrangeItems, () => {
        itemSyncSetLastUpdate()
      })
    }
  }

  onResetOrder = () => {
    const { items } = this.props

    this.initRearrangeItems(items)
  }

  onDragEnd = result => {
    const { arrangeItems } = this.state

    if (!result.destination) {
      return
    }

    const items = this.reorder(arrangeItems, result.source.index, result.destination.index)

    this.setState({ arrangeItems: items, hasArranged: true })
  }

  reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)

    const [removed] = result.splice(startIndex, 1)

    result.splice(endIndex, 0, removed)

    return result
  }

  onDelete = item => {
    const { storeId, deleteItem, itemSyncSetLastUpdate } = this.props

    const confirm = window.confirm(`Are you sure to delete (${item.itemData.name || 'Unknown'})?`)

    if (confirm) {
      deleteItem(storeId, item)
      itemSyncSetLastUpdate()
    }
  }

  onEdit = item => {
    const dataToEdit = {
      key: item.id,
      name: item.itemData.name || initItemData.name,
      current_price: item.itemData.current_price || initItemData.current_price,
      description: item.itemData.description || initItemData.description,
      tag: item.itemData.tag || initItemData.tag,
      status: item.itemData.status || initItemData.status,
      options: item.itemData.options,
      code: item.itemData.code || initItemData.code,
    }

    const arrangeOptions = []
    _.forEach(item.itemData.options || {}, (option, optionKey) => {
      const order = arrangeOptions[option.order] ? option.order + 100 : option.order
      arrangeOptions[order] = optionKey
    })

    this.setState({
      modalVisible: true,
      itemData: dataToEdit,
      action: 'edit',
      arrangeOptions: arrangeOptions.filter(Boolean),
    })
  }

  onAddClick = () => {
    this.setState({ modalVisible: true, itemData: initItemData, action: 'new' })
  }

  onUpload = item => {
    this.setState({ uploadData: item, uploadVisible: true })
  }

  onClose = () => {
    this.setState({ modalVisible: false, itemData: null, action: null, arrangeOptions: [] })
  }

  onUploadClose = () => {
    this.setState({ uploadVisible: false, uploadData: null })
  }

  onFieldChange = (field, value) => {
    let { itemData } = this.state

    itemData = {
      ...itemData,
      [field]: value,
    }

    this.setState({ itemData })
  }

  onRemoveOption = key => {
    const { arrangeOptions: prevArrangeOptions } = this.state

    const arrangeOptions = prevArrangeOptions.filter(optionKey => optionKey !== key)

    this.setState({ arrangeOptions })
  }

  onOptionOrderChange = (currentIndex, direction) => {
    const { arrangeOptions: prevArrangeOptions } = this.state
    const arrangeOptions = Array.from(prevArrangeOptions)
    const lastOptionIndex = prevArrangeOptions.length - 1

    if (direction === 'up' && currentIndex === 0) return

    if (direction === 'down' && currentIndex === lastOptionIndex) return

    const destinationIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    const targetKey = arrangeOptions[currentIndex]
    const destinationKey = arrangeOptions[destinationIndex]

    arrangeOptions[currentIndex] = destinationKey
    arrangeOptions[destinationIndex] = targetKey

    this.setState({ arrangeOptions })
  }

  onSaveItem = () => {
    const { action, itemData, arrangeOptions } = this.state
    const { storeId, saveItem, setItemOptions, itemSyncSetLastUpdate } = this.props
    let itemId

    if (action === 'edit') {
      itemId = itemData.key
    } else {
      itemId = firebase
        .database()
        .ref(`items/${storeId}`)
        .push().key
    }

    saveItem(storeId, itemId, itemData, async () => {
      const itemOptions = itemData.options || {}

      if (action === 'edit' && Object.keys(itemOptions).length > 0) {
        const updates = {}
        const optionKeys = Object.keys(itemOptions || {})
        const removeOptionKeys = optionKeys.filter(key => !arrangeOptions.includes(key))

        removeOptionKeys.forEach(optionKey => {
          updates[`items/${storeId}/${itemId}/options/${optionKey}`] = null
        })

        arrangeOptions.forEach((optionKey, index) => {
          updates[`items/${storeId}/${itemId}/options/${optionKey}/order`] = index
        })

        await firebase
          .database()
          .ref()
          .update(updates)

        const options = arrangeOptions.reduce((result, optionKey, index) => {
          const newResult = result
          newResult[optionKey] = {
            ...itemData.options[optionKey],
            order: index,
          }

          return newResult
        }, {})

        setItemOptions(storeId, itemId, options)
      }
      itemSyncSetLastUpdate()
      this.onClose()
    })
  }

  onSyncToHasura = async () => {
    const { storeId, syncMerchantItems } = this.props

    await syncMerchantItems(storeId)
  }

  renderLoading = () => <Placeholder />

  renderItem = (item, index) => {
    const { arranging, itemOptions } = this.props
    const { showImage } = this.state

    return (
      <DraggableItem
        key={item.id}
        item={item}
        itemOptions={itemOptions}
        index={index}
        saving={arranging}
        showImage={showImage === 'show'}
        getItemDraggableStyle={this.getItemDraggableStyle}
        onDelete={() => this.onDelete(item)}
        onEdit={() => this.onEdit(item)}
        itemTag={() => this.getItemTag(item)}
        onUpload={() => this.onUpload(item)}
      />
    )
  }

  renderItems = () => {
    const { arrangeItems } = this.state

    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable droppableId="droppable">
          {dropProvided => (
            <div ref={dropProvided.innerRef} className="drag-drop-restaunrant-item-wrapper">
              {_.map(arrangeItems, (item, index) => this.renderItem(item, index))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    )
  }

  renderNoItems = () => {
    return (
      <Table className="no-border" style={{ fontSize: 16 }}>
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={4} textAlign="center" style={{ padding: 40 }}>
              <Header size="small">No data available</Header>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    )
  }

  renderList = () => {
    const { storeId, setImages, tags, adding, storeData, itemOptions, itemSync, itemSyncLoading } = this.props
    const {
      uploadVisible,
      uploadData,
      arrangeItems,
      arrangeOptions,
      arranging,
      modalVisible,
      itemData,
      action,
      hasArranged,
      showImage,
    } = this.state
    const { last_updated_at: lastUpdatedAt, synced_at: syncedAt } = itemSync || {}
    const syncable = _.isEmpty(itemSync) || _.isEmpty(syncedAt) || syncedAt < lastUpdatedAt
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0px 14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Header style={{ marginBottom: 0 }}>Items ({_.size(arrangeItems)})</Header>
            <Button icon onClick={this.onToggleShowImage} style={{ marginLeft: 16 }}>
              <Icon name={showImage === 'hide' ? 'square outline' : 'check square outline'} /> Show Image
            </Button>
          </div>
          <div style={{ display: 'flex' }}>
            {hasArranged && (
              <Button color="black" disabled={arranging} onClick={this.onResetOrder} icon>
                <Icon name="undo" />
                Reset Arrangement
              </Button>
            )}

            {hasArranged && (
              <Button
                color="black"
                disabled={!hasArranged || arranging}
                loading={arranging}
                onClick={this.onSaveOrder}
                icon
              >
                <Icon name="checkmark" />
                Save Arrangement
              </Button>
            )}

            <Button icon color="black" onClick={this.onAddClick}>
              <Icon name="add" /> Add New Item
            </Button>

            <Button icon color="green" onClick={this.onSyncToHasura} disabled={!syncable || itemSyncLoading}>
              <Icon name="sync" /> Sync Now
            </Button>
          </div>
        </div>
        <div>
          <Divider style={{ margin: 14 }} />
        </div>
        <div style={{ display: 'flex', flex: 1, overflow: 'auto' }} className="branches-summary">
          {_.size(arrangeItems) > 0 ? this.renderItems() : this.renderNoItems()}
        </div>
        {itemData && (
          <ItemForm
            storeData={storeData}
            tags={tags}
            loading={adding}
            visible={modalVisible}
            itemData={itemData}
            itemOptions={itemOptions}
            arrangeOptions={arrangeOptions}
            onClose={this.onClose}
            action={action}
            onFieldChange={this.onFieldChange}
            onOptionOrderChange={this.onOptionOrderChange}
            onRemoveOption={this.onRemoveOption}
            saveItem={this.onSaveItem}
          />
        )}
        {uploadData && (
          <ItemImageCropUploader
            visible={uploadVisible}
            uploadData={uploadData}
            label="Image"
            onClose={this.onUploadClose}
            onUploadComplete={urls => setImages(storeId, uploadData.id, urls)}
            compressWidth={250}
            compressHeight={250}
            uploadPath={`items/${uploadData.id}`}
            dbPath={`items/${storeId}/${uploadData.id}`}
          />
        )}
      </div>
    )
  }

  render() {
    const { items, loading } = this.props

    if (loading || !items) {
      return this.renderLoading()
    }

    if (items) {
      return this.renderList()
    }

    return null
  }
}

const mapStateToProps = ({ items, itemOptions, itemSync }, { storeId }) => ({
  adding: items.adding,
  itemSync: itemSync.data,
  itemSyncLoading: itemSync.loading,

  arranging: items.arranging,
  itemOptions: itemOptions.data[storeId] || {},
})

const mapDispatch = {
  getItemSync: ItemSyncActions.getItemSync,
  syncMerchantItems: ItemSyncActions.syncMerchantItems,
  itemSyncSetLastUpdate: ItemSyncActions.setLastUpdate,
  deleteItem: ItemActions.deleteItem,
  saveItem: ItemActions.saveItem,
  saveOrder: ItemActions.saveArrangement,
  setImages: ItemActions.setImages,
  setItemOptions: ItemActions.setItemOptions,
}

export default connect(
  mapStateToProps,
  mapDispatch
)(RestaurantItems)



// WEBPACK FOOTER //
// ./src/components/restaurants/views/itemsList.js